import React, { Component } from 'react';
/**
 * CheatSheetDrawer — Bottom sheet drawer for the Spiekbriefje (hint system).
 *
 * Slides up from the bottom with spring animation. Renders SubSkillCheatSheet
 * which handles its own empty state. Includes focus trap, escape-to-close,
 * body scroll lock and swipe-to-dismiss on the drag handle.
 *
 * Speech bubble treatment
 * -----------------------
 * A small "note from Djay" speech bubble FLOATS just above the drawer's top
 * edge, with its tail pointing down at the Djay avatar in the drawer header.
 * The bubble is rendered as a child of the drawer motion element, absolutely
 * positioned at `top: 0` and translated upward, so it slides into view with
 * the drawer and stays anchored to Djay regardless of viewport height.
 *
 * The bubble's job is the *affective coaching opener* — a single short line
 * that sounds like Djay is talking. The actual didactic content (rules,
 * pitfalls, examples) lives in the sticky-note hint cards below. This split
 * mirrors the APP_FEEL 2027 principle P2 ("Djay is the emotional layer")
 * and avoids the previous duplication where the bubble repeated the KERN
 * hint verbatim.
 *
 * Bubble content priority:
 *   1. `FAMILY_SUPPORT_REGISTRY[familyId].bubbleHint`  ← authored, preferred
 *   2. Derived from `defaultExplanation` if short enough (<90 chars)
 *   3. Fallback coaching nudge
 *
 * Visual treatment
 * ----------------
 * The drawer surface is a warm cream "paper" colour with an amber drag-tab
 * and a faint ruled line under the header, so the whole thing reads like a
 * real cheat sheet / notebook page rather than a generic system dialog.
 *
 * Co-located in support/components/ (RC Ronde 5 — S-3: verhuisd uit
 * practice/components/. De drawer is een UI-shell rond een support-renderer
 * en hoort daarom in support/.)
 */
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { type ModuleId, MODULES } from '../../content';
import { SubSkillCheatSheet } from './SubSkillCheatSheet';
import { useFocusTrap } from '../../app/hooks/useFocusTrap';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { useBodyScrollLock } from '../../app/hooks/useBodyScrollLock';
import { useSwipeToDismiss } from '../../app/hooks/useSwipeToDismiss';
import { DjayAvatar, useDjaySelection } from '../../mascot';
import { FAMILY_SUPPORT_REGISTRY } from '..';
// ── Coaching fallbacks ─────────────────────────────────────
// A short rotating set keeps the empty-family case from feeling templated.
// We pick deterministically based on familyId hash so the same family always
// gets the same line within a session.
const FALLBACK_COACHINGS = ['Even rustig lezen. Wat weet je al?', 'Geen paniek — kijk wat je nog weet, en probeer een stap.', 'Spieken mag! Pak één hint die je verder helpt.', 'Lees nog eens. Wat is de vraag écht?'];
function pickFallback(seed: string | undefined): string {
  if (!seed) return FALLBACK_COACHINGS[0];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = h * 31 + seed.charCodeAt(i) | 0;
  return FALLBACK_COACHINGS[Math.abs(h) % FALLBACK_COACHINGS.length];
}
/**
 * Derive a short opener from a long `defaultExplanation`. Returns null when
 * the source is too long / taxonomic to make a clean bubble — caller should
 * fall back to a generic coaching line instead of dumping the whole text.
 */
function deriveOpenerFromExplanation(text: string | undefined): string | null {
  if (!text) return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  // First sentence
  const sentenceMatch = trimmed.match(/^(.{0,110}?[.!?])(\s|$)/);
  const firstSentence = sentenceMatch ? sentenceMatch[1] : trimmed;
  // Reject taxonomic / enumerative openings — these duplicate the KERN card
  // and break the conversational feel. Heuristic: lots of commas or a colon
  // in the first 40 chars usually means "X: a, b, c" style content.
  const head = firstSentence.slice(0, 40);
  const commaCount = (firstSentence.match(/,/g) || []).length;
  if (head.includes(':') || commaCount >= 3) return null;
  // Cap length — bubble must stay one breath.
  if (firstSentence.length > 100) return null;
  return firstSentence;
}
// ── Types ────────────────────────────────────────────────────
interface CheatSheetDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  moduleId: ModuleId;
  subSkill?: string;
  familyId?: string;
}
// ── Component ────────────────────────────────────────────────
export function CheatSheetDrawer({
  isOpen,
  onClose,
  moduleId,
  subSkill,
  familyId
}: CheatSheetDrawerProps) {
  const focusTrapRef = useFocusTrap(isOpen);
  const {
    selectedVariant
  } = useDjaySelection();
  useEscapeKey(onClose, isOpen);
  useBodyScrollLock(isOpen);
  const swipe = useSwipeToDismiss({
    onDismiss: onClose,
    disabled: !isOpen
  });
  // Bubble copy: authored bubbleHint wins, else derive a short opener,
  // else use a deterministic coaching fallback.
  const registryEntry = familyId ? FAMILY_SUPPORT_REGISTRY[familyId] : undefined;
  const bubbleMessage = registryEntry?.bubbleHint?.trim() || deriveOpenerFromExplanation(registryEntry?.defaultExplanation) || pickFallback(familyId);
  const moduleTitle = MODULES.find(m => m.id === moduleId)?.title ?? moduleId;
  return <AnimatePresence data-id="element-12253">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40" data-id="element-12254" />

          {/* Bottom Sheet — paper-feel surface.
                        The floating bubble is rendered as an absolutely positioned
                        CHILD of this motion element so it slides in WITH the drawer
                        and stays visually anchored to Djay. */}
          <motion.div ref={focusTrapRef} role="dialog" aria-modal="true" aria-labelledby="cheatsheet-title" initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 32,
        stiffness: 350
      }} {...swipe.dragProps} className="fixed bottom-0 left-0 right-0 z-50 bg-[#fdfaf2] rounded-t-3xl shadow-2xl max-h-[78vh] flex flex-col" style={{
        // Subtle warm paper-edge shadow on top
        boxShadow: '0 -8px 24px -12px rgba(120, 53, 15, 0.18), 0 -1px 0 rgba(251, 191, 36, 0.18)',
        // CRITICAL: allow the floating Djay bubble (absolute child with
        // `bottom: 100%`) to render OUTSIDE the rounded top edge. Without
        // this, some browsers / nested stacking contexts could clip the
        // bubble back into the drawer area on top of Djay. Belt-and-
        // braces fix for the v1.0 overlap regression.
        overflow: 'visible'
      }} data-id="element-12255">
            {/* Floating speech bubble — sits OUTSIDE the rounded top edge of
                the drawer (top:0 + translate -100%) so it visually pops up
                above Djay's head. Animated in slightly after the drawer
                settles so the eye lands on Djay first, then the line. */}
            <motion.div key={`bubble-${familyId ?? 'no-family'}`} initial={{
          opacity: 0,
          y: 12,
          scale: 0.85,
          rotate: -3
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1,
          rotate: -1.2
        }} exit={{
          opacity: 0,
          y: 8,
          scale: 0.9
        }} transition={{
          type: 'spring',
          stiffness: 360,
          damping: 24,
          delay: 0.22
        }} role="status" aria-live="polite"
        /* Anchor: bubble's BOTTOM edge sits at the drawer's TOP edge
        (`bottom-full`). `mb-3` adds explicit 12px breathing room so
        the tail clearly floats ABOVE the drawer surface and never
        touches the drag-handle area. Combined with the parent
        motion.div's `overflow: visible`, this guarantees the bubble
        renders outside Djay's avatar regardless of viewport size. */ className="absolute left-3 bottom-full mb-3 pointer-events-none origin-bottom-left z-10" data-id="element-12256">
              <div className="pointer-events-auto inline-block relative max-w-[min(320px,calc(100vw-2rem))]" data-id="element-12257">
                {/* Note — warm off-white with a hand-noted feel, lifted with
                 a stronger drop-shadow so it visually reads as floating
                 ABOVE the drawer, not as part of it. */}
                <div className="relative bg-white rounded-2xl rounded-bl-md border border-amber-100 shadow-[0_12px_28px_-12px_rgba(120,53,15,0.32),0_4px_8px_-4px_rgba(120,53,15,0.12)] px-3.5 py-2.5" data-id="element-12258">
                  {/* "Djay zegt" micro-label */}
                  <div className="flex items-center gap-1 mb-0.5" data-id="element-12259">
                    <span aria-hidden="true" className="text-[9px] font-bold uppercase tracking-[0.12em] text-amber-600" data-id="element-12260">
                      Djay zegt
                    </span>
                    <span aria-hidden="true" className="text-amber-400 text-[10px] leading-none" data-id="element-12261">
                      ✦
                    </span>
                  </div>
                  <p className="text-[13px] sm:text-[13.5px] font-medium text-slate-800 leading-snug" data-id="element-12262">
                    {bubbleMessage}
                  </p>
                </div>

                {/* Tail — pointing down-left at Djay's avatar */}
                <svg aria-hidden="true" width="20" height="14" viewBox="0 0 20 14" className="absolute -bottom-[11px] left-4" style={{
              filter: 'drop-shadow(0 2px 1px rgba(120, 53, 15, 0.08))'
            }} data-id="element-12263">
                  <path d="M0 0 L20 0 L4 13 Z" fill="white" stroke="rgb(254 243 199)" strokeWidth="1" data-id="element-12264" />
                  {/* Hide stroke along the top so it merges with the bubble */}
                  <path d="M1 0 L19 0" stroke="white" strokeWidth="2" data-id="element-12265" />
                </svg>
              </div>
            </motion.div>

            {/* Decorative ruled line under the header — notebook page feel */}
            <div aria-hidden="true" className="absolute top-[64px] left-0 right-0 h-px bg-amber-200/45 pointer-events-none" data-id="element-12266" />

            {/* Drag Handle — only this region initiates swipe-to-dismiss so
                the inner scrollable content keeps scrolling on touch. */}
            <div {...swipe.handleProps} onClick={onClose} className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing flex-shrink-0" role="button" aria-label="Sleep omlaag of tik om te sluiten" data-id="element-12267">
              <div className="w-10 h-1.5 rounded-full bg-amber-300/70" data-id="element-12268" />
            </div>

            {/* Header — Djay sits at top-left so the floating bubble's tail
                naturally points at him. */}
            <div className="flex items-center justify-between gap-3 px-5 pb-3 flex-shrink-0" data-id="element-12269">
              <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-12270">
                <div className="flex-shrink-0" data-id="element-12271">
                  <DjayAvatar size="md" emotion="pointing" variant={selectedVariant} data-id="element-12272" />
                </div>

                <div className="min-w-0" data-id="element-12273">
                  <h2 id="cheatsheet-title" className="text-lg font-extrabold text-slate-900 leading-tight tracking-tight" data-id="element-12274">
                    Spiekbriefje
                  </h2>
                  <p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider mt-0.5 truncate" data-id="element-12275">
                    {moduleTitle}
                    {subSkill ? ` · ${subSkill}` : ''}
                  </p>
                </div>
              </div>

              <button onClick={onClose} className="p-1.5 hover:bg-amber-100 rounded-full transition-colors text-slate-500 hover:text-slate-700 flex-shrink-0" aria-label="Sluiten" data-id="element-12276">
                <X className="w-5 h-5" data-id="element-12277" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 pt-3 pb-6" data-id="element-12278">
              <SubSkillCheatSheet familyId={familyId} data-id="element-12279" />
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}